import { useShowcasePhotos } from "@/hooks/useShowcasePhotos";
import { useState, useEffect, useRef } from "react";

/**
 * ShowcaseGallery
 * A staggered grid of tiles, each independently swapping to a new random photo
 * every ~5–10 seconds. Only renders if the admin has uploaded photos.
 */
export function ShowcaseGallery({ tileCount = 6, compact = false }) {
  const photos = useShowcasePhotos();

  if (photos.length === 0) return null;
  // Cap tiles at number of photos to avoid duplicates, but ensure at least 3
  const count = Math.max(3, Math.min(tileCount, photos.length));

  // Build initial unique starting indices
  const initialIndices = [];
  const shuffled = [...Array(photos.length).keys()].sort(() => Math.random() - 0.5);
  for (let i = 0; i < count; i++) initialIndices.push(shuffled[i % photos.length]);

  if (compact) {
    // Inline variant — no section chrome / header; caller controls context.
    return (
      <div
        className={`grid gap-2 ${count <= 4 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`}
        data-testid="showcase-gallery-compact"
      >
        {initialIndices.map((startIdx, tileI) => (
          <ShowcaseTile
            key={tileI}
            photos={photos}
            startIdx={startIdx}
            intervalMs={5000 + Math.floor(Math.random() * 5000)}
            initialDelay={tileI * 1200}
          />
        ))}
      </div>
    );
  }

  return (
    <section className="relative py-16 sm:py-20 bg-[#050505] border-t border-[#1A1A1A]" data-testid="showcase-gallery">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">In The Wild</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Recent Builds
            </h2>
          </div>
          <p className="font-mono text-[10px] text-[#A1A1A1]/60 max-w-xs">
            A rotating look at what we&apos;re shipping. Photos change every few seconds.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-4">
          {initialIndices.map((startIdx, tileI) => (
            <ShowcaseTile
              key={tileI}
              photos={photos}
              startIdx={startIdx}
              // Stagger between 4s and 10s per tile so they don't flip together
              intervalMs={5000 + Math.floor(Math.random() * 5000)}
              // Delay the first swap so tiles don't all start at once
              initialDelay={tileI * 1200}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ShowcaseTile({ photos, startIdx, intervalMs, initialDelay }) {
  const [current, setCurrent] = useState(startIdx);
  const [previous, setPrevious] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (photos.length < 2) return;
    const startTimer = () => {
      timerRef.current = setInterval(() => {
        setCurrent((c) => {
          setPrevious(c);
          let next = Math.floor(Math.random() * photos.length);
          if (next === c) next = (next + 1) % photos.length;
          return next;
        });
      }, intervalMs);
    };
    const startTimeout = setTimeout(startTimer, initialDelay);
    return () => {
      clearTimeout(startTimeout);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [photos.length, intervalMs, initialDelay]);

  const curPhoto = photos[current % photos.length];
  const prevPhoto = previous != null ? photos[previous % photos.length] : null;

  return (
    <div className={`relative aspect-square overflow-hidden border border-[#1A1A1A] bg-[#0D0D0D] group`}>
      {prevPhoto && (
        <img
          key={`p-${prevPhoto.id}`}
          src={prevPhoto.image_url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <img
        key={`c-${curPhoto.id}`}
        src={curPhoto.image_url}
        alt={curPhoto.caption || ""}
        className="absolute inset-0 w-full h-full object-cover animate-fade-in-slow"
        style={{ animation: "fadeIn 1.5s ease-in-out" }}
      />
      {curPhoto.caption && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#050505]/90 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <p className="font-mono text-[10px] uppercase tracking-wider text-[#F2F2F2]">{curPhoto.caption}</p>
        </div>
      )}
    </div>
  );
}
